import { html, css } from 'lit-element';
import { BaseElement } from './BaseElement';
import { Columns } from './Datatable';
import * as GUID from '../GUID';
import { API, EVENT_TYPES, LOGIN_STATUS_LOGGED_IN, LOGIN_STATUS_LOGGED_OUT } from '../API';
import { State } from '../State';
import { DIALOG_TYPE_ERROR } from './ModalDialog';

const _stateId = Symbol();
const _api = Symbol();
const _state = Symbol();
const _columns = Symbol();
const _apiLoginStatusHandler = Symbol();

const DATATABLE_STATE_ID = '2992809E-0B03-47A0-A719-86631B65A48F';

export class PatientSearch extends BaseElement {
	constructor() {
		super();

		this[_api] = null;
		this[_state] = null;
		this[_columns] = Columns.fromArray([
			{ name: 'CustomerName', display: 'Patient Name' },
			{ name: 'SurgeryDate', display: 'Surgery Date' },
			{ name: 'StopDate', display: 'Stop Date' }
		]);
		this[_apiLoginStatusHandler] = () => {
			switch(this.api.loginStatus) {
				case LOGIN_STATUS_LOGGED_IN:
					this.requestUpdate();
					if(!this.state.get(DATATABLE_STATE_ID))
						this.search('CustomerName', true, 0);
					break;
				case LOGIN_STATUS_LOGGED_OUT:
					this.requestUpdate();
					this.datatable.totalRecords = 0;
					this.datatable.pageNumber = 0;
					this.datatable.rows = [];
					break;
			}
		};
	}
	static get styles() {
		return [
			...BaseElement.styles,
			css`
				:host {
					
				}
				form.filters {
					display: flex;
					flex-flow: row wrap;
					justify-content: center;
				}
				form.filters input, form.filters select {
					margin: 0.25em;
					background-color: var(--theme-background-hover, red);
				}
				fieldset {
					margin-bottom: 1em;
				}
				legend {
					padding: 0 0.5em;
					border-bottom: none;
				}
			`
		];
	}
	get api() {
		return this[_api];
	}
	set api(v) {
		if(!(v instanceof API))
			throw new TypeError('api must be an instance of API.');
		this[_api] = v;
	}
	get stateId() {
		return this[_stateId];
	}
	set stateId(v) {
		if(v !== null && typeof v !== 'string')
			throw new TypeError('stateId must be a string.');
		this[_stateId] = v;
	}
	get state() {
		return this[_state];
	}
	set state(v) {
		if(v !== null && !(v instanceof State))
			throw new TypeError('state must be an instance of State.');
		this[_state] = v;
	}
	get datatable() {
		return this.shadowRoot.querySelector('pp-datatable');
	}
	firstUpdated(changedProperties) {
		super.firstUpdated(changedProperties);
		if(!this.api)
			throw new Error('No API property set.');
		
		if(this.stateId) {
			let state = this.state.get(this.stateId);
			if(state)
				this.setFilters(state);
		}
		if(this.api.loginStatus === LOGIN_STATUS_LOGGED_IN && !this.state.get(DATATABLE_STATE_ID))
			this.search('CustomerName', true, 0);
	}
	connectedCallback() {
		super.connectedCallback();

		this.emit('get-api', { callback: api => this.api = api });
		this.emit('get-state', { callback: state => this.state = state });

		
		//add event listeners
		this.api.on(EVENT_TYPES.EVENT_TYPE_LOGIN_STATUS, this[_apiLoginStatusHandler]);
	}
	disconnectedCallback() {
		super.disconnectedCallback();

		//remove event listeners
		this.api.off(null, this[_apiLoginStatusHandler]);

		this[_api] = null;
	}
	getFilters() {
		return Object.fromEntries(new FormData(this.shadowRoot.querySelector('form.filters')));
	}
	setFilters(obj) {
		if(typeof obj !== 'object')
			throw new TypeError('obj must be an object.');
		if(!obj) return;

		const elements = this.shadowRoot.querySelector('form.filters').elements;
		Object.keys(obj).forEach(key => {
			const element = elements[key];
			if(!element) return;
			element.value = obj[key];
		});
	}
	async formChangeHandler() {
		this.state.set(this.stateId, this.getFilters(), true);
		const datatable = this.shadowRoot.querySelector('pp-datatable');
		
		await this.search(
			datatable.sortColumn,
			datatable.sortAsc,
			datatable.pageNumber,
			this.getFilters()
		);
	}
	async search(sortColumn, sortAsc, pageNumber, filters) {
		const id = GUID.newGUID();
		try {
			this.emit('show-busy', id);
			if(typeof sortColumn !== 'string')
				throw new TypeError('sortColumn must be a string.');
			if(typeof sortAsc !== 'boolean')
				throw new TypeError('sortASC must be a boolean.');
			if(!Number.isInteger(pageNumber))
				throw new TypeError('pageNumber must be an integer.');
			
			const searchResult = await this.api.searchPatients({
				sortColumn: sortColumn,
				sortAsc,
				pageNumber,
				filters
			});
			this.datatable.totalRecords = searchResult.total;
			this.datatable.pageSize = searchResult.pageSize;
			this.datatable.pageNumber = searchResult.pageNumber;
			this.datatable.rows = searchResult.records;
			
			this.emit('hide-busy', id);
		}
		catch(err) {
			console.error(err);
			this.emit('hide-busy', id);
			this.emit('show-dialog', {
				title: 'Patient Search Error',
				message: err instanceof TypeError ? 'Failed to connect to data service.' : err.message,
				type: DIALOG_TYPE_ERROR
			});
		}
	}
	render() {
		return html`
			<div class="${this.api.loggedIn ? '' : 'no-display'}">
				<fieldset class="filters">
					<legend>Filters</legend>
					<form @submit="${e => e.preventDefault()}" @change="${() => this.formChangeHandler()}" class="filters">
						<select name="PostSurgery">
							<option value="">All Patients</option>
							<option value="1">0 to 30 Days Post Surgery</option>
							<option value="2">31 to 60 Days Post Surgery</option>
							<option value="3">61+ Days Post Surgery</option>
						</select>
						<input type="text" name="CustomerName" placeholder="Patient Name" />
					</form>
				</fieldset>
				
				<pp-datatable
					.columns="${this[_columns]}"
					.stateId="${DATATABLE_STATE_ID}"
					@refresh-data="${e => this.search(e.detail.sortColumn, e.detail.sortAsc, e.detail.pageNumber, this.getFilters())}"
					@row-click="${(e) => this.emit('patient-selected', e.detail)}"
				></pp-datatable>
			</div>
			<div class="${this.api.loggedIn ? 'no-display' : ''}">
				<h4>Patient Search is unavailable.</h4>
				 <p>You are currently logged out</p>
			</div>
		`;
	}
}

customElements.define('pp-patient-search', PatientSearch);